@if (paginator.state$ | async; as paginatorState) {
	<div
		name="table-container"
		class="bg-base-100 card border-base-200 border shadow-sm w-full"
	>
		<table
			name="table-body"
			class="table w-full"
		>
			<thead class="bg-base-200/50 text-base-content/60 text-xs font-bold tracking-wider uppercase">
				<tr>
					<!-- selection header -->
					<th class="selection-column w-12 text-center">
						<div class="flex justify-center">
							{{ itemsSelector.numSelected ? itemsSelector.numSelected : '' }}
						</div>
					</th>

					<!-- Icon placeholder -->
					<th class="w-12"></th>

					<!-- name header -->
					<th class="py-4">
						<div class="flex items-center gap-2">
							<span
								class="text-nowrap"
								[translate]
								>Name</span
							>
							<div class="join">
								<button
									class="btn btn-ghost btn-xs join-item"
									(click)="paginator.setOrderingBy('name')"
									title="Sort"
								>
									↑↓
								</button>
								<button
									class="btn btn-ghost btn-xs join-item"
									[class.btn-active]="displaySearchByFields$_['name']"
									(click)="toggleSearchByField('name')"
									title="Search"
								>
									🔎
								</button>
							</div>
						</div>
						@if (displaySearchByFields$_['name']) {
							<div class="mt-2">
								<input
									class="input input-bordered input-xs focus:input-primary w-full max-w-xs"
									type="text"
									placeholder="Search..."
									[(ngModel)]="filterByFields$_['name']"
								/>
							</div>
						}
					</th>

					<!-- date header -->
					<th class="py-4">
						<div class="flex items-center gap-2">
							<span
								class="text-nowrap"
								[translate]
								>Date</span
							>
							<button
								class="btn btn-ghost btn-xs"
								(click)="paginator.setOrderingBy('time_created')"
							>
								↑↓
							</button>
						</div>
					</th>

					<!-- actions header -->
					<th class="w-20 py-4 text-center">
						<span [translate]>Actions</span>
					</th>
				</tr>
			</thead>
			<tbody>
				@for (folder of paginatorState.itemsOnCurrentPage; track folder?.id) {
					@if (folder) {
						<tr
							[tabIndex]="0"
							(click)="processClick(folder)"
							(keyup.enter)="processClick(folder)"
							class="hover:bg-base-200/50 cursor-pointer transition-colors duration-150"
							[ngClass]="{ 'bg-primary/10': itemsSelector.isSelected(folder) }"
						>
							<!-- selection -->
							<td class="text-center">
								<div
									class="flex justify-center"
									(click)="$event.stopImmediatePropagation(); itemsSelector.toggle(folder)"
									(keydown)="(undefined)"
									tabindex="0"
								>
									<input
										class="checkbox checkbox-primary checkbox-sm"
										[name]="'selectedItems' + folder.id"
										type="checkbox"
										[checked]="itemsSelector.isSelected(folder)"
										tabindex="0"
									/>
								</div>
							</td>

							<!-- icon -->
							<td class="text-center">
								<div class="text-secondary flex justify-center">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										fill="currentColor"
										class="h-6 w-6"
									>
										<path d="M19.5 21a3 3 0 003-3v-4.5a3 3 0 00-3-3h-15a3 3 0 00-3 3V18a3 3 0 003 3h15zM1.5 10.146V6a3 3 0 013-3h5.379a2.25 2.25 0 011.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 013 3v1.146A4.483 4.483 0 0019.5 9h-15a4.483 4.483 0 00-3 1.146z" />
									</svg>
								</div>
							</td>

							<!-- name -->
							<td>
								<div class="text-base-content font-medium">
									{{ folder.name }}
								</div>
							</td>

							<!-- date -->
							<td>
								<div class="text-base-content/60 text-xs">
									{{ folder.timeCreated | date: 'mediumDate' }}
								</div>
							</td>

							<!-- actions -->
							<td class="text-center">
								<div class="join">
									<button
										class="btn btn-ghost btn-xs join-item text-warning"
										(click)="$event.stopPropagation(); renameFolder(folder)"
										title="Rename"
									>
										<!-- Rename Icon -->
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 20 20"
											fill="currentColor"
											class="h-4 w-4"
										>
											<path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
											<path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
										</svg>
									</button>
									<button
										class="btn btn-ghost btn-xs join-item text-error"
										(click)="$event.stopPropagation(); deleteFolder(folder)"
										title="Delete"
									>
										<!-- Delete Icon -->
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 20 20"
											fill="currentColor"
											class="h-4 w-4"
										>
											<path
												fill-rule="evenodd"
												d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
												clip-rule="evenodd"
											/>
										</svg>
									</button>
								</div>
							</td>
						</tr>

						<!-- Expanded row -->
						@if (openedItems.isSelected(folder)) {
							<tr
								class="bg-base-200/20"
								fullSpanRow
							>
								<td
									colspan="5"
									class="relative p-8"
								>
									<button
										class="btn btn-ghost btn-xs absolute top-2 right-2"
										(click)="openedItems.unselect(folder)"
									>
										<span
											class="text-xs italic"
											[translate]
											>close display</span
										>
									</button>
									<pre class="bg-base-300 overflow-auto rounded p-2 text-xs">{{ folder | json }}</pre>
								</td>
							</tr>
						}
					}
				} @empty {
					<tr>
						<td
							colspan="5"
							class="text-base-content/50 py-12 text-center italic"
						>
							<span [translate]>Nothing here</span>
						</td>
					</tr>
				}
			</tbody>
		</table>

		<!-- Paginator -->
		@if (paginatorState.totalNumberOfPages > 1) {
			<div
				name="table-footer"
				class="bg-base-200/30 border-base-200 flex items-center justify-between border-t p-4"
			>
				<div class="join">
					<button
						(click)="paginatorState.hasPrev ? paginator.previousPage() : null"
						[disabled]="!paginatorState.hasPrev"
						class="btn btn-sm join-item"
					>
						«
					</button>
					<button class="btn btn-sm join-item no-animation cursor-default">
						<span class="text-xs font-medium">Page {{ paginatorState.currentPage }} / {{ paginatorState.totalNumberOfPages }}</span>
					</button>
					<button
						(click)="paginatorState.hasNext ? paginator.nextPage() : null"
						[disabled]="!paginatorState.hasNext"
						class="btn btn-sm join-item"
					>
						»
					</button>
				</div>
				<div class="text-base-content/40 text-xs font-medium tracking-tight uppercase">Total: {{ paginatorState.totalNumberOfItems }} items</div>
			</div>
		}
	</div>
}
